import { useCallback, useMemo, useRef, useState } from 'react';

export interface UseFiltersReturn<T> {
  /** Valores en edición (borrador): lo que el usuario ve en los controles. */
  draft: T;
  /** Valores aplicados: los que produjeron la consulta actual. */
  applied: T;
  /** Actualiza un criterio del borrador. */
  setDraft: <K extends keyof T>(key: K, value: T[K]) => void;
  /** Reemplaza todo el borrador. */
  setDraftAll: (value: T) => void;
  /** Copia el borrador a los filtros aplicados. */
  apply: () => void;
  /** Devuelve borrador y aplicados a los filtros iniciales. */
  clear: () => void;
  /** Si el borrador tiene al menos un criterio cargado (habilita "Aplicar"). */
  hasCriteria: boolean;
  /** Si el borrador difiere de los filtros aplicados. */
  isDirty: boolean;
}

function hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return value;
  return true;
}

function hasAnyCriteria<T extends object>(filters: T): boolean {
  return Object.values(filters).some(hasValue);
}

function shallowEqual<T extends object>(a: T, b: T): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    const av = (a as Record<string, unknown>)[key];
    const bv = (b as Record<string, unknown>)[key];
    if (Array.isArray(av) && Array.isArray(bv)) {
      if (av.length !== bv.length || av.some((item, index) => item !== bv[index])) return false;
    } else if (av !== bv) {
      return false;
    }
  }
  return true;
}

/**
 * Maneja el estado de filtros en dos capas: borrador y aplicados.
 * El estado vive en el componente que lo usa, por lo que se reinicia
 * automáticamente al desmontarse (salir de la sección).
 */
export function useFilters<T extends object>(initial: T): UseFiltersReturn<T> {
  const initialRef = useRef(initial);
  const [draft, setDraftState] = useState<T>(initial);
  const [applied, setApplied] = useState<T>(initial);

  const setDraft = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setDraftState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setDraftAll = useCallback((value: T) => {
    setDraftState(value);
  }, []);

  const apply = useCallback(() => {
    setApplied(draft);
  }, [draft]);

  const clear = useCallback(() => {
    setDraftState(initialRef.current);
    setApplied(initialRef.current);
  }, []);

  const hasCriteria = useMemo(() => hasAnyCriteria(draft), [draft]);
  const isDirty = useMemo(() => !shallowEqual(draft, applied), [draft, applied]);

  return { draft, applied, setDraft, setDraftAll, apply, clear, hasCriteria, isDirty };
}
